import * as React from 'react';
import { useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import BasicContentBox from '@root/components/v2/content/BasicContentBox';
import './style.scss';

const FunnelChart = ({
  title = 'Funnel Chart',
  subtitle,
  labels = [],
  subLabels = [],
  colors = [],
  values = [],
  width = 2000,
  height = 100,
  gradientDirection = 'horizontal',
  direction = 'horizontal',
  onBlockClick = null,
  header = null,
  footer = null,
  rightSideContent = null,
  containerStyles,
  innerStyles,
  chartStyles,
}) => {
  const funnelRef = useRef(null);

  // Two-dimensional funnel chart data structure
  // Matching the exact categories and styling from the "Runtime exploit funnel" image
  const funnelGraphData = useMemo(
    () => ({
      labels: labels,
      subLabels: subLabels,
      colors: colors,
      values: values,
    }),
    [colors, labels, subLabels, values],
  );

  useEffect(() => {
    let scriptElement = null;

    const initFunnel = () => {
      if (funnelRef.current && typeof window !== 'undefined') {
        // Clear any existing content
        funnelRef.current.innerHTML = '';

        // Check if FunnelGraph is already loaded
        if (window.FunnelGraph) {
          createFunnelGraph();
          return;
        }

        // Check if script is already loaded
        const existingScript = document.querySelector('script[src*="funnel-graph.min.js"]');
        if (existingScript) {
          // Script already exists, just wait for it to load
          setTimeout(() => {
            if (window.FunnelGraph) {
              createFunnelGraph();
            }
          }, 100);
          return;
        }

        // Load funnel-graph-js from CDN since the npm package has module issues
        scriptElement = document.createElement('script');
        scriptElement.src = 'https://unpkg.com/funnel-graph-js@1.4.2/dist/js/funnel-graph.min.js';
        scriptElement.onload = () => {
          // Add a small delay to ensure DOM is ready
          setTimeout(() => {
            createFunnelGraph();
          }, 100);
        };

        scriptElement.onerror = () => {
          console.error('Failed to load funnel-graph-js from CDN');
          // Fallback: show labels with basic styling
          if (funnelRef.current) {
            renderFallbackLabels();
          }
        };

        document.head.appendChild(scriptElement);

        // CSS is now handled by local SCSS file which imports the downloaded CSS files
      }
    };

    const renderFallbackLabels = () => {
      if (!funnelRef.current || !labels.length) return;

      const labelsHtml = labels
        .map((label, index) => {
          const value = values[index] || '';
          // `colors` is one colour per stage, or a single colour for the whole funnel.
          const color = (Array.isArray(colors) ? colors[index] : colors) || '#e2e8f0';
          const isTotal = label === 'Total';
          const isClickable = onBlockClick && !isTotal;
          const cursorStyle = isClickable ? 'pointer' : 'default';
          const hoverEffects = isClickable
            ? `onmouseover="this.style.transform='scale(1.05)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.2)';" onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='none';"`
            : '';

          return `
          <div class="fallback-label" data-index="${index}" data-label="${label}" style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 12px;
            margin: 4px;
            background-color: ${color};
            border-radius: 8px;
            min-width: 120px;
            text-align: center;
            cursor: ${cursorStyle};
            transition: transform 0.2s, box-shadow 0.2s;
          "
          ${hoverEffects}>
            <div class="label-title" style="
              font-size: 14px;
              font-weight: 600;
              color: #1a202c;
              margin-bottom: 4px;
            ">${label}</div>
            <div class="label-value" style="
              font-size: 16px;
              font-weight: 700;
              color: #000000;
            ">${value}</div>
          </div>
        `;
        })
        .join('');

      funnelRef.current.innerHTML = `
        <div style="
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          width: 100%;
          height: 100%;
          padding: 20px 20px 0 20px;
          margin-top: 20px;
        ">
          ${labelsHtml}
        </div>
      `;

      // Add click listeners to fallback labels (skip "Total")
      if (onBlockClick) {
        const fallbackLabels = funnelRef.current.querySelectorAll('.fallback-label');
        fallbackLabels.forEach(labelElement => {
          const labelText = labelElement.getAttribute('data-label');

          // Skip click handler for "Total" labels
          if (labelText === 'Total') {
            return;
          }

          labelElement.addEventListener('click', e => {
            const index = parseInt(e.currentTarget.getAttribute('data-index'), 10);
            onBlockClick({
              label: labels[index],
              value: values[index],
              index: index,
            });
          });
        });
      }
    };

    const createFunnelGraph = () => {
      try {
        // Ensure the container element exists and is ready
        if (!funnelRef.current) {
          console.error('Container element not found');
          return;
        }

        // Try using a unique ID selector instead of the DOM element
        const containerId = `funnel-container-${Date.now()}`;
        funnelRef.current.id = containerId;

        console.log('Using container ID:', containerId);

        // Create the funnel graph using the global FunnelGraph with selector string
        // Following the exact configuration from the documentation
        const graph = new window.FunnelGraph({
          container: `#${containerId}`,
          gradientDirection: gradientDirection, // Applied to all segments with gradients
          data: funnelGraphData,
          displayPercent: false, // Hide percentage display
          displayLegend: false, // Hide legend (Critical, High, Other)
          direction: direction,
          width: width,
          height: height,
        });

        graph.draw();

        // Add click handlers to the funnel segments after drawing (skip "Total")
        if (onBlockClick) {
          setTimeout(() => {
            const funnelPaths = funnelRef.current.querySelectorAll('.svg-funnel-js__path');
            const funnelLabels = funnelRef.current.querySelectorAll('.svg-funnel-js__label');
            const funnelValues = funnelRef.current.querySelectorAll('.svg-funnel-js__value');

            // Add click handlers to paths (the funnel segments)
            funnelPaths.forEach((path, index) => {
              // Skip "Total" labels
              if (labels[index] === 'Total') {
                path.style.cursor = 'default';
                return;
              }

              path.style.cursor = 'pointer';
              path.style.transition = 'opacity 0.2s ease';

              // Add hover effects
              path.addEventListener('mouseenter', () => {
                path.style.opacity = '0.8';
              });
              path.addEventListener('mouseleave', () => {
                path.style.opacity = '1';
              });

              // Add click handler
              path.addEventListener('click', () => {
                onBlockClick({
                  label: labels[index],
                  value: values[index],
                  index: index,
                });
              });
            });

            // Add click handlers to labels
            funnelLabels.forEach((label, index) => {
              // Skip "Total" labels
              if (labels[index] === 'Total') {
                label.style.cursor = 'default';
                return;
              }

              label.style.cursor = 'pointer';
              label.addEventListener('click', () => {
                onBlockClick({
                  label: labels[index],
                  value: values[index],
                  index: index,
                });
              });
            });

            // Add click handlers to values
            funnelValues.forEach((valueElement, index) => {
              // Skip "Total" labels
              if (labels[index] === 'Total') {
                valueElement.style.cursor = 'default';
                return;
              }

              valueElement.style.cursor = 'pointer';
              valueElement.addEventListener('click', () => {
                onBlockClick({
                  label: labels[index],
                  value: values[index],
                  index: index,
                });
              });
            });
          }, 200);
        }
      } catch (error) {
        console.error('Error creating funnel graph:', error);
        console.error('Error details:', error.message);
        // Fallback: show a simple message
        if (funnelRef.current) {
          funnelRef.current.innerHTML =
            '<div style="text-align: center; padding: 20px; color: #666;">Error loading funnel chart</div>';
        }
      }
    };

    initFunnel();

    // Cleanup function
    return () => {
      if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, [direction, funnelGraphData, gradientDirection, height, width, colors, labels, values, onBlockClick]);

  return (
    <BasicContentBox
      title={title}
      subtitle={subtitle}
      rightSideContent={rightSideContent}
      style={containerStyles}
      innerStyles={{
        height: footer ? 'auto' : '210px',
        padding: '20px 10px',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        ...(innerStyles || {}),
      }}>
      {header}
      <div
        className="runtime-exploit-funnel"
        style={{
          ...(footer ? { height: '170px', width: '100%', overflow: 'hidden' } : {}),
          ...(chartStyles || {}),
        }}>
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div ref={funnelRef} className="funnel-container" />
        </Box>
      </div>
      {footer}
    </BasicContentBox>
  );
};

export default FunnelChart;
